import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGetApiCsvSchemasId } from '@/api/gen/api';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack,
  CalendarToday,
  TableChart,
  CheckCircle,
  Cancel,
  Key,
  Schedule,
} from '@mui/icons-material';

export default function CsvSchemasShow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  useEffect(() => {
    // Verificar si venimos desde el create con el mensaje de éxito
    if (location.state?.showSuccessMessage) {
      setShowSuccessAlert(true);
      // Limpiar el estado de navegación
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const { data, error, isLoading } = useGetApiCsvSchemasId(Number(id));

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDataTypeColor = (dataType?: string) => {
    switch (dataType) {
      case 'String':
        return 'primary';
      case 'Int':
        return 'success';
      case 'Double':
        return 'info';
      case 'Bool':
        return 'warning';
      case 'DateTime':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity='error'>Error al cargar el esquema CSV</Alert>
      </Box>
    );
  }

  const schema = data;

  if (!schema) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity='warning'>No se encontró el esquema</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header con botón de volver */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/CsvSchemas')}
          variant='outlined'
        >
          Volver
        </Button>
        <Typography variant='h4' component='h1'>
          Detalle del Esquema CSV
        </Typography>
      </Box>

      {/* Información General del Schema */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant='h5' gutterBottom sx={{ mb: 3 }}>
          Información General
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant='outlined'>
              <CardContent>
                <Typography color='text.secondary' gutterBottom>
                  ID del Esquema
                </Typography>
                <Typography variant='h6'>{schema.id}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant='outlined'>
              <CardContent>
                <Typography color='text.secondary' gutterBottom>
                  Nombre del Esquema
                </Typography>
                <Typography variant='h6'>{schema.name || 'N/A'}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant='outlined'>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarToday color='action' />
                  <Typography color='text.secondary'>
                    Fecha de Creación
                  </Typography>
                </Box>
                <Typography variant='h6' sx={{ mt: 1 }}>
                  {formatDate(schema.createdUtc)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant='outlined'>
              <CardContent>
                <Typography color='text.secondary' gutterBottom>
                  ID de Planta
                </Typography>
                <Typography variant='h6'>{schema.plantId || 'N/A'}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant='outlined'>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TableChart color='action' />
                  <Typography color='text.secondary'>
                    Total de Columnas
                  </Typography>
                </Box>
                <Typography variant='h6' sx={{ mt: 1 }}>
                  {schema.columns?.length || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla de Columnas */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant='h5' gutterBottom sx={{ mb: 2 }}>
          Columnas del Esquema
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {schema.columns && schema.columns.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    ID
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Encabezado
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Tipo de Dato
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{ color: 'white', fontWeight: 'bold' }}
                  >
                    Requerido
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{ color: 'white', fontWeight: 'bold' }}
                  >
                    Clave Primaria
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{ color: 'white', fontWeight: 'bold' }}
                  >
                    Timestamp
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schema.columns.map((column, index) => (
                  <TableRow
                    key={column.id}
                    sx={{
                      '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                      '&:hover': { bgcolor: 'action.selected' },
                    }}
                  >
                    <TableCell>
                      <Chip label={column.id} size='small' variant='outlined' />
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight='medium'>
                        {column.header}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={column.dataType}
                        color={getDataTypeColor(column.dataType)}
                        size='medium'
                      />
                    </TableCell>
                    <TableCell align='center'>
                      {column.isRequired ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <CheckCircle color='error' />
                          <Chip label='Sí' color='error' size='small' />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <Cancel color='disabled' />
                          <Chip label='No' variant='outlined' size='small' />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align='center'>
                      {column.isPrimary ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <Key color='primary' />
                          <Chip label='Sí' color='primary' size='small' />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <Cancel color='disabled' />
                          <Chip label='No' variant='outlined' size='small' />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align='center'>
                      {column.isTimestamp ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <Schedule color='warning' />
                          <Chip label='Sí' color='warning' size='small' />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <Cancel color='disabled' />
                          <Chip label='No' variant='outlined' size='small' />
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity='info'>
            Este esquema no tiene columnas definidas
          </Alert>
        )}
      </Paper>

      {/* Snackbar de éxito */}
      <Snackbar
        open={showSuccessAlert}
        autoHideDuration={6000}
        onClose={() => setShowSuccessAlert(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowSuccessAlert(false)}
          severity='success'
          variant='filled'
          sx={{
            width: '100%',
            fontSize: '1rem',
            fontWeight: 'bold',
            boxShadow: 4,
          }}
        >
          ✅ ¡Esquema CSV creado con éxito!
        </Alert>
      </Snackbar>
    </Box>
  );
}
